import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import "../Css/Main.css";

interface Message {
  from: "bot" | "user";
  text: string;
}

type Step =
  | "menu"
  | "name"
  | "email"
  | "phone"
  | "projectType"
  | "budget"
  | "details"
  | "done";

interface Lead {
  name: string;
  email: string;
  phone: string;
  projectType: string;
  budget: string;
  details: string;
}

const PROJECT_TYPES = [
  "Web Development",
  "Mobile App",
  "AI / Machine Learning",
  "Blockchain / Web3",
  "Metaverse",
  "Logistics Software",
  "Other",
];

const BUDGETS = ["Under $1,000", "$1,000 - $5,000", "$5,000 - $20,000", "$20,000+", "Not sure yet"];

const WHATSAPP_NUMBER = "923344403514";
const CONTACT_EMAIL = "info@spacexpakistan.com";
const API_BASE = "/chatbot-api";

const SITE_PAGES = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Services", path: "/services" },
  { label: "Portfolio", path: "/portfolio" },
  { label: "Blog", path: "/blog" },
  { label: "Contact", path: "/contactUs" },
  { label: "Web Development", path: "/webdev" },
  { label: "Mobile Apps", path: "/appdev" },
  { label: "Artificial Intelligence", path: "/artificialIntellegence" },
  { label: "Metaverse", path: "/metaverse" },
  { label: "Logistics", path: "/logistics" },
];

// Keyword-based answers for each service (longer keywords take priority in matchIntent)
const KNOWLEDGE: { keywords: string[]; answer: string }[] = [
  {
    keywords: [
      "web development", "website", "web app", "web application", "frontend",
      "backend", "full stack", "fullstack", "react", "wordpress", "ecommerce",
      "e-commerce", "online store", "landing page", "portal", "web dev",
    ],
    answer:
      "We offer professional Web Development — fast, responsive websites and web apps including e-commerce, business portals, and landing pages. Our team works with modern stacks like React and full-stack solutions. Tap 'Start a Project' for a free quote!",
  },
  {
    keywords: [
      "mobile app", "mobile application", "android", "ios", "iphone", "ipad",
      "react native", "flutter", "app development", "app dev", "play store",
      "app store", "cross platform", "cross-platform",
    ],
    answer:
      "We build high-quality Mobile Apps for Android and iOS — native, React Native, and Flutter. From healthcare apps like OMIE to social platforms like VETgram, we deliver great user experiences. Tap 'Start a Project' to share your app idea!",
  },
  {
    keywords: [
      "artificial intelligence", "machine learning", "deep learning",
      "chatbot", "ai solution", "ai development", "nlp", "computer vision",
      "data science", "predictive", "automation", "neural network", "openai",
      "generative ai", "llm",
    ],
    answer:
      "We develop Artificial Intelligence solutions — machine learning, chatbots, automation, NLP, computer vision, and predictive analytics for industries like healthcare, finance, and logistics. Tap 'Start a Project' to discuss your AI requirements!",
  },
  {
    keywords: [
      "blockchain", "web3", "web 3", "smart contract", "cryptocurrency",
      "crypto", "nft", "defi", "decentralized", "token", "solidity",
      "ethereum", "dapp", "d-app", "wallet", "ico",
    ],
    answer:
      "We specialize in Blockchain & Web3 development — smart contracts, DApps, NFT marketplaces, crypto wallets, DeFi platforms, and token solutions. We've built products like NFT marketplaces and fintech platforms. Tap 'Start a Project' to get started!",
  },
  {
    keywords: [
      "metaverse", "virtual world", "virtual reality", "vr", "ar ",
      "augmented reality", "3d experience", "digital twin", "immersive",
    ],
    answer:
      "We build Metaverse solutions — immersive 3D experiences, virtual worlds, VR/AR applications, and digital ownership platforms. Tap 'Start a Project' to explore how we can bring your metaverse idea to life!",
  },
  {
    keywords: [
      "logistics", "transport", "fleet management", "shipment tracking",
      "route optimization", "supply chain", "warehouse", "delivery app",
      "freight", "trucking", "gps tracking",
    ],
    answer:
      "We deliver Logistics & Transport software — fleet management, route optimization, real-time shipment tracking, and supply chain solutions to help your business move goods faster and more efficiently. Tap 'Start a Project' for a custom solution!",
  },
  {
    keywords: [
      "staff augmentation", "hire developers", "dedicated team", "outsource",
      "outsourcing", "remote developers", "extend team", "contract developers",
      "onshore", "offshore", "off-shore",
    ],
    answer:
      "We offer Staff Augmentation & outsourcing — hire skilled developers (onshore or offshore) to scale your team quickly while you focus on your core business. Tap 'Start a Project' or contact us to discuss your team needs!",
  },
  {
    keywords: [
      "ico marketing", "ico launch", "token sale", "crypto marketing",
      "blockchain marketing", "token promotion",
    ],
    answer:
      "We provide ICO Marketing services — end-to-end token launch campaigns, community building, and promotional strategies to make your ICO a success. Tap 'Start a Project' to discuss your launch plan!",
  },
  {
    keywords: [
      "digital marketing", "seo", "social media marketing", "google ads",
      "facebook ads", "lead generation", "online marketing", "ppc",
      "content marketing", "email marketing", "roi",
    ],
    answer:
      "Our Digital Marketing services help grow your business — SEO, social media, paid ads, lead generation, and strategies focused on sales and ROI. Tap 'Start a Project' to boost your online presence!",
  },
  {
    keywords: [
      "ui design", "ux design", "ui/ux", "user interface", "user experience",
      "figma", "wireframe", "prototype", "app design", "web design",
    ],
    answer:
      "We offer UI/UX Design — modern, user-centric designs for web and mobile apps. We create wireframes, prototypes, and polished interfaces that deliver great user experiences. Tap 'Start a Project' to get started!",
  },
  {
    keywords: [
      "complaint management", "cms software", "ticketing system", "helpdesk",
      "customer support system", "issue tracking",
    ],
    answer:
      "Yes! We build Complaint Management Systems (CMS) with admin dashboards, complaint tracking, department assignment, reports, and analytics — like the CMS in our portfolio. Tap 'Start a Project' to discuss yours!",
  },
  {
    keywords: [
      "healthcare app", "health app", "mental health", "hospital software",
      "medical app", "telemedicine", "patient management",
    ],
    answer:
      "We develop healthcare and mental health apps — mood tracking, health metrics, personalized sessions, and patient management. Check OMIE in our portfolio! Tap 'Start a Project' for your healthcare solution.",
  },
  {
    keywords: [
      "erp", "crm", "management system", "saas", "enterprise software",
      "custom software", "business software",
    ],
    answer:
      "We build custom business software — ERP, CRM, SaaS platforms, and management systems tailored to your workflow. Tap 'Start a Project' and describe what you need!",
  },
  {
    keywords: ["service", "what do you do", "what do you offer", "offer", "provide", "expertise"],
    answer:
      "SpaceX Pakistan offers:\n• Web Development\n• Mobile Apps (Android & iOS)\n• Artificial Intelligence\n• Blockchain & Web3\n• Metaverse\n• Logistics & Transport\n• Staff Augmentation\n• ICO Marketing\n• Digital Marketing\n• UI/UX Design\n\nTap 'Start a Project' or ask about any specific service!",
  },
  {
    keywords: ["price", "cost", "charge", "rate", "how much", "pricing", "quote", "estimate"],
    answer:
      "Project cost depends on scope and complexity. Tap 'Start a Project' and describe what you need — our team will get back to you with a free quote within 24 hours.",
  },
  {
    keywords: ["time", "how long", "deadline", "duration", "delivery", "timeline", "turnaround"],
    answer:
      "Timelines vary by project — a simple website takes 2–4 weeks, mobile apps and larger systems take a few months. Share your requirements via 'Start a Project' and we'll give you a realistic estimate.",
  },
  {
    keywords: ["portfolio", "previous work", "past work", "examples", "case study", "clients", "projects you built"],
    answer:
      "Our portfolio includes OMIE (mental healthcare app), VETgram (veterans social app), a Complaint Management System, NFT marketplaces, fintech platforms, and more. Visit the Portfolio page or ask about a specific type of project!",
  },
  {
    keywords: ["contact", "email", "phone", "number", "whatsapp", "reach", "call", "get in touch"],
    answer: `Reach us at ${CONTACT_EMAIL} or WhatsApp/call +92-334-4403514. Or tap 'Start a Project' and we'll contact you within 24 hours.`,
  },
  {
    keywords: ["where", "location", "office", "based", "address", "pakistan"],
    answer:
      "We are SpaceX Pakistan — a software house based in Pakistan, serving clients worldwide remotely.",
  },
  {
    keywords: ["hi", "hello", "hey", "salam", "assalam", "good morning", "good evening"],
    answer:
      "Hello! Welcome to SpaceX Pakistan. Ask me about any of our services — Web, Mobile, AI, Blockchain, Metaverse, Logistics, and more — or tap 'Start a Project' to share your requirements.",
  },
];

const FALLBACK =
  "I'm not sure about that, but our team can help! Tap 'Start a Project' to share your requirements, or ask me about our services, pricing, or portfolio.";

const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [step, setStep] = useState<Step>("menu");
  const [typing, setTyping] = useState(false);
  const [lead, setLead] = useState<Lead>({
    name: "",
    email: "",
    phone: "",
    projectType: "",
    budget: "",
    details: "",
  });
  const bottomRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing, open]);

  useEffect(() => {
    if (open && messages.length === 0) {
      botSay(
        "Hi there! Welcome to SpaceX Pakistan. I'm your virtual assistant. Ask about our services, browse any page using the buttons below, or tap 'Start a Project' to share your requirements."
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const botSay = (text: string, delay = 600) => {
    setTyping(true);
    setTimeout(() => {
      setMessages((prev) => [...prev, { from: "bot", text }]);
      setTyping(false);
    }, delay);
  };

  const userSay = (text: string) => {
    setMessages((prev) => [...prev, { from: "user", text }]);
  };

  const matchIntent = (text: string): string => {
    const lower = text.toLowerCase();
    let best: { answer: string; score: number } | null = null;

    for (const item of KNOWLEDGE) {
      for (const keyword of item.keywords) {
        const k = keyword.toLowerCase();
        if (lower.includes(k)) {
          const score = k.length;
          if (!best || score > best.score) {
            best = { answer: item.answer, score };
          }
        }
      }
    }

    return best ? best.answer : FALLBACK;
  };

  const logQuery = (question: string, answer: string | null, source: string) => {
    fetch(`${API_BASE}/log.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ question, answer, source }),
    }).catch(() => {});
  };

  // Answer from the built-in knowledge base first; if unknown, ask the
  // backend for a learned answer. All questions are saved in the admin query log.
  const smartReply = async (text: string) => {
    const local = matchIntent(text);
    if (local !== FALLBACK) {
      logQuery(text, local, "builtin");
      botSay(local);
      return;
    }
    setTyping(true);
    try {
      const res = await fetch(`${API_BASE}/ask.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: text }),
      });
      const data = await res.json();
      setTyping(false);
      botSay(data && data.answer ? data.answer : FALLBACK, 300);
    } catch {
      setTyping(false);
      logQuery(text, FALLBACK, "fallback");
      botSay(FALLBACK, 300);
    }
  };

  const submitLead = (finalLead: Lead) => {
    fetch(`${API_BASE}/lead.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(finalLead),
    }).catch(() => {
      /* lead can still be sent via the WhatsApp/email buttons */
    });
  };

  const startProject = () => {
    userSay("Start a Project");
    setStep("name");
    botSay("Great! Let's get some details so our team can reach you. First, what's your name?");
  };

  const goToPage = (label: string, path: string) => {
    userSay(`Go to ${label}`);
    botSay(`Opening ${label}...`);
    setTimeout(() => {
      navigate(path);
      setOpen(false);
    }, 700);
  };

  const renderPageNav = () => (
    <div className="chatbot-nav-scroll px-3 pb-2">
      <p className="text-[11px] text-gray-400 mb-2 uppercase tracking-wide">Browse pages</p>
      <div className="flex flex-wrap gap-2">
        {SITE_PAGES.map((page) => (
          <button
            key={page.path}
            onClick={() => goToPage(page.label, page.path)}
            className="chatbot-chip"
          >
            {page.label}
          </button>
        ))}
      </div>
    </div>
  );

  const handleOption = (option: string) => {
    if (step === "projectType") {
      userSay(option);
      setLead((l) => ({ ...l, projectType: option }));
      setStep("budget");
      botSay("Got it. Do you have a budget range in mind?");
      return;
    }
    if (step === "budget") {
      userSay(option);
      setLead((l) => ({ ...l, budget: option }));
      setStep("details");
      botSay(
        "Now the important part - please describe your project requirements. Include features you need, target users, and anything else that helps us understand your idea."
      );
      return;
    }
  };

  const buildSummary = (finalLead: Lead) => {
    return (
      `New Project Inquiry (via website chatbot)%0A%0A` +
      `Name: ${encodeURIComponent(finalLead.name)}%0A` +
      `Email: ${encodeURIComponent(finalLead.email)}%0A` +
      `Phone: ${encodeURIComponent(finalLead.phone || "Not provided")}%0A` +
      `Project Type: ${encodeURIComponent(finalLead.projectType)}%0A` +
      `Budget: ${encodeURIComponent(finalLead.budget)}%0A%0A` +
      `Requirements:%0A${encodeURIComponent(finalLead.details)}`
    );
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    userSay(text);

    switch (step) {
      case "menu":
        smartReply(text);
        break;
      case "name":
        setLead((l) => ({ ...l, name: text }));
        setStep("email");
        botSay(`Nice to meet you, ${text}! What's your email address?`);
        break;
      case "email":
        if (!/\S+@\S+\.\S+/.test(text)) {
          botSay("That doesn't look like a valid email. Please enter a valid email address (e.g. you@example.com).");
          return;
        }
        setLead((l) => ({ ...l, email: text }));
        setStep("phone");
        botSay("Thanks! What's your phone/WhatsApp number? (or type 'skip')");
        break;
      case "phone":
        setLead((l) => ({ ...l, phone: text.toLowerCase() === "skip" ? "" : text }));
        setStep("projectType");
        botSay("What type of project do you have in mind? Pick one below or type your own.");
        break;
      case "projectType":
        setLead((l) => ({ ...l, projectType: text }));
        setStep("budget");
        botSay("Got it. Do you have a budget range in mind?");
        break;
      case "budget":
        setLead((l) => ({ ...l, budget: text }));
        setStep("details");
        botSay(
          "Now the important part - please describe your project requirements. Include features you need, target users, and anything else that helps us understand your idea."
        );
        break;
      case "details": {
        const finalLead = { ...lead, details: text };
        setLead(finalLead);
        setStep("done");
        submitLead(finalLead);
        botSay(
          "Perfect, I've got everything and forwarded your requirements to our team - we usually respond within 24 hours. You can also reach us instantly using the buttons below."
        );
        break;
      }
      case "done":
        smartReply(text);
        break;
    }
  };

  const reset = () => {
    setMessages([]);
    setStep("menu");
    setLead({ name: "", email: "", phone: "", projectType: "", budget: "", details: "" });
    botSay("Chat restarted! How can I help you? Ask about our services or tap 'Start a Project'.");
  };

  const quickOptions = () => {
    if (step === "menu") {
      return (
        <>
          <div className="flex flex-wrap gap-2 px-3 pb-2">
            <button onClick={startProject} className="chatbot-chip chatbot-chip-primary">
              Start a Project
            </button>
            <button
              onClick={() => {
                userSay("Our Services");
                botSay(matchIntent("service"));
              }}
              className="chatbot-chip"
            >
              Our Services
            </button>
            <button
              onClick={() => {
                userSay("Contact Info");
                botSay(matchIntent("contact"));
              }}
              className="chatbot-chip"
            >
              Contact Info
            </button>
          </div>
          {renderPageNav()}
        </>
      );
    }
    if (step === "projectType") {
      return (
        <div className="flex flex-wrap gap-2 px-3 pb-2">
          {PROJECT_TYPES.map((t) => (
            <button key={t} onClick={() => handleOption(t)} className="chatbot-chip">
              {t}
            </button>
          ))}
        </div>
      );
    }
    if (step === "budget") {
      return (
        <div className="flex flex-wrap gap-2 px-3 pb-2">
          {BUDGETS.map((b) => (
            <button key={b} onClick={() => handleOption(b)} className="chatbot-chip">
              {b}
            </button>
          ))}
        </div>
      );
    }
    if (step === "done") {
      const summary = buildSummary(lead);
      return (
        <>
          <div className="flex flex-wrap gap-2 px-3 pb-2">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${summary}`}
              target="_blank"
              rel="noreferrer"
              className="chatbot-chip chatbot-chip-primary"
            >
              Send via WhatsApp
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
                `Project Inquiry: ${lead.projectType}`
              )}&body=${summary}`}
              className="chatbot-chip chatbot-chip-primary"
            >
              Send via Email
            </a>
            <button onClick={reset} className="chatbot-chip">
              Start Over
            </button>
          </div>
          {renderPageNav()}
        </>
      );
    }
    return null;
  };

  return createPortal(
    <div className="chatbot-widget">
      {/* Floating toggle button */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Open AI assistant"
        className="chatbot-fab"
      >
        {open ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        )}
      </button>

      {/* Chat window */}
      {open && (
        <div className="chatbot-panel">
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-customRed to-neonPurple flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
              S
            </div>
            <div>
              <p className="text-white font-semibold leading-tight">SpaceX AI Agent</p>
              <p className="text-white/80 text-xs leading-tight">Typically replies instantly</p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                    m.from === "user"
                      ? "bg-customRed text-white rounded-br-sm"
                      : "bg-white/10 text-gray-100 rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div className="bg-white/10 text-gray-300 px-3 py-2 rounded-2xl rounded-bl-sm text-sm">
                  <span className="animate-pulse">typing...</span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick reply chips */}
          {!typing && quickOptions()}

          {/* Input */}
          <div className="p-3 border-t border-white/10 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              placeholder="Type a message..."
              className="flex-1 bg-white/5 border border-white/15 text-white placeholder-gray-500 text-sm rounded-lg px-3 py-2 focus:border-customRed focus:outline-none"
            />
            <button
              onClick={handleSend}
              aria-label="Send message"
              className="px-3 rounded-lg bg-customRed text-white hover:opacity-90 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default ChatBot;
